if (!customElements.get('tooltip-component')) {
  class TooltipComponent extends HTMLElement {
    constructor() {
      super();
      this.trigger = null;
      this.iconTrigger = null;
      this.content = null;
      this._bubble = null;
      this._reflow = null;
    }

    connectedCallback() {
      this.init();
      this.initEvents();
    }

    disconnectedCallback() {
      // cleanup
      document.removeEventListener('click', this._docClick, true);
      window.removeEventListener('scroll', this._reflow);
      window.removeEventListener('resize', this._reflow);
      if (this._bubble && this._bubble.parentNode === document.body) {
        document.body.removeChild(this._bubble);
      }
    }

    init() {
      this.content = this.querySelector('template')?.innerHTML || '';
      this.trigger = this.querySelector('.tooltip-trigger') || this;
      this.iconTrigger = this.querySelector('.tooltip-trigger') || this;

      if (!this._bubble) {
        const bubble = document.createElement('div');
        bubble.className = 'tooltip-bubble pos-bottom';

        // Copy class from tooltip-component to bubble for custom styling
        if (this.className) {
          bubble.classList.add(...this.className.split(' '));
        }

        if (this.getAttribute('html') === 'true') {
          bubble.innerHTML = this.content;
        } else {
          bubble.innerHTML = this.content;

          const iconClose = bubble.querySelector('.tooltip-icon-close');
          if (iconClose) {
            iconClose.addEventListener('click', (e) => {
              e.stopPropagation();
              this.removeAttribute('data-open');
              this._bubble.style.display = 'none';
            });
          }
        }
        bubble.style.display = 'none';
        document.body.appendChild(bubble);
        this._bubble = bubble;
      }
    }

    initEvents() {
      const isTouchDevice =
        'ontouchstart' in window || window.matchMedia('(pointer: coarse)').matches;

      if (!isTouchDevice) {
        let closeTimer = null;

        const openTooltip = () => {
          if (closeTimer) {
            clearTimeout(closeTimer);
            closeTimer = null;
          }
          this.setAttribute('data-open', 'true');
          this.caculatePosition();
          this._bubble.style.display = 'block';
        };

        const closeTooltip = () => {
          closeTimer = setTimeout(() => {
            this.removeAttribute('data-open');
            this._bubble.style.display = 'none';
            closeTimer = null;
          }, 0);
        };

        this.trigger.addEventListener('mouseenter', openTooltip);
        this.trigger.addEventListener('mouseleave', closeTooltip);

        this._bubble.addEventListener('mouseenter', openTooltip);
        this._bubble.addEventListener('mouseleave', closeTooltip);
      }

      if (isTouchDevice) {
        this.trigger.addEventListener('click', this.handleClick.bind(this));
      }

      this._docClick = (e) => {
        if (this._bubble?.style.display !== 'block') return;
        const insideTrigger = this.contains(e.target);
        const insideBubble = this._bubble.contains(e.target);
        if (!insideTrigger && !insideBubble) {
          this.removeAttribute('data-open');
          this._bubble.style.display = 'none';
        }
      };
      document.addEventListener('click', this._docClick, true);

      // sticky position scroll/resize
      this._reflow = () => {
        if (this.getAttribute('data-open') === 'true') {
          this.caculatePosition();
        }
      };
      window.addEventListener('scroll', this._reflow, { passive: true });
      window.addEventListener('resize', this._reflow);

      // prevent close when click on bubble
      this._bubble.addEventListener('click', (e) => {
        e.stopPropagation();
      });
    }

    handleClick(e) {
      e.stopPropagation();
      const opened = this.getAttribute('data-open') === 'true';

      // close all other bubble
      document.querySelectorAll('tooltip-component[data-open="true"]').forEach((el) => {
        if (el !== this) el.removeAttribute('data-open');
      });
      document.querySelectorAll('.tooltip-bubble').forEach((b) => {
        if (b !== this._bubble) b.style.display = 'none';
      });

      if (!opened) {
        this.setAttribute('data-open', 'true');

        const latest = this.querySelector('template')?.innerHTML || '';
        if (latest && latest !== this.content) {
          this.content = latest;
          if (this.getAttribute('html') === 'true') {
            this._bubble.innerHTML = this.content;
          } else {
            this._bubble.textContent = this.content;
          }
        }
        this.caculatePosition();
        this._bubble.style.display = 'block';
      } else {
        this.removeAttribute('data-open');
        this._bubble.style.display = 'none';
      }
    }

    caculatePosition() {
      if (!this._bubble) return;

      const MARGIN = 8;
      const GAP_DESKTOP = 8;
      const GAP_MOBILE = 4;
      const WIDTH_MOBILE_MAX = 768;

      const b = this._bubble;
      const rect = this.trigger.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;

      // Reset to calculate size
      b.style.left = '0px';
      b.style.top = '-10000px';
      b.classList.remove('pos-top', 'pos-bottom');

      // Display temporarily to calculate size
      const prevDisplay = b.style.display;
      const prevVisibility = b.style.visibility;
      b.style.visibility = 'hidden';
      b.style.display = 'block';

      const bw = b.offsetWidth;
      const bh = b.offsetHeight;

      // Calculate GAP by device
      const GAP = vw <= WIDTH_MOBILE_MAX ? GAP_MOBILE : GAP_DESKTOP;

      // Check if this tooltip should always show above (e.g., volume-pricing-tooltip)
      const forcePosTop = this.classList.contains('volume-pricing-tooltip-component');

      // Calculate top tooltip (prefer below trigger, unless forced above)
      let top;
      if (forcePosTop && rect.top - GAP - bh >= MARGIN) {
        // Force above for volume-pricing tooltip
        top = rect.top - bh - GAP;
        b.classList.add('pos-top');
      } else {
        // Default logic: prefer below trigger
        top = rect.bottom + GAP;
        if (top + bh > vh - MARGIN && rect.top - GAP - bh >= MARGIN) {
          top = rect.top - bh - GAP;
          b.classList.add('pos-top');
        } else {
          b.classList.add('pos-bottom');
        }
      }

      // Calculate left for tooltip
      let left;
      if (vw <= WIDTH_MOBILE_MAX) {
        // Mobile: center viewport horizontally
        left = vw / 2 - bw / 2;
        if (left < MARGIN) left = MARGIN;
        if (left + bw > vw - MARGIN) left = vw - MARGIN - bw;
      } else {
        // Desktop: center trigger horizontally
        left = rect.left + rect.width / 2 - bw / 2;
        if (left < MARGIN) left = MARGIN;
        if (left + bw > vw - MARGIN) left = vw - MARGIN - bw;
      }

      // Apply coordinates
      b.style.left = left + 'px';
      b.style.top = top + 'px';

      // Calculate arrow position (arrow), center by trigger, clamp in tooltip content
      const padSafe = 10,
        radius = 6,
        guard = 6;
      let arrowX = rect.left + rect.width / 2 - left;
      const minArrow = padSafe + radius + guard;
      const maxArrow = bw - (padSafe + radius + guard);
      if (arrowX < minArrow) arrowX = minArrow;
      if (arrowX > maxArrow) arrowX = maxArrow;

      b.style.setProperty('--arrow-x', arrowX + 'px');

      // Restore display state
      b.style.visibility = prevVisibility || 'visible';
      b.style.display = prevDisplay === 'none' ? 'none' : prevDisplay || 'block';
    }
  }

  customElements.define('tooltip-component', TooltipComponent);
}
